import { z } from 'zod';
import { IsoDate } from './envelope';
import { Uuid } from './envelope';

/**
 * The agent estate — /v2/agents.
 *
 * Every other screen in this product answers "what have we got, who owns it,
 * and what can we prove about it". This one answers it for the things that act
 * on the estate rather than sit in it, and the shapes below exist to stop it
 * becoming the one screen that asks to be believed.
 *
 * **Autonomy is the field everything else hangs off.** An agent that drafts for
 * review and an agent that acts unattended are different risks wearing the same
 * noun, and no amount of detail about the model matters more than which of the
 * two is in front of you.
 *
 * **Scope is governance, not branding.** A personal agent reaches what one
 * person can already reach and nothing downstream consumes its output. A
 * corporate agent runs on a team's behalf and other work depends on it. The
 * first mostly needs to be *known about*; the second needs an owner, an
 * evaluation and a way to stop it.
 *
 * **An unevaluated agent says so.** `accuracy` is nullable and
 * `evaluationAbsentReason` is required whenever it is null — the same rule the
 * database enforces in `agent_evaluation_stated`. A blank here would let a
 * reader supply their own optimism about a thing deciding on confidential data.
 */

export const AgentScope = z.enum(['Personal', 'Corporate']);
export type AgentScope = z.infer<typeof AgentScope>;

/** Suggests, acts with a human in the loop, or acts unattended. */
export const AgentAutonomy = z.enum(['Suggests', 'ActsWithApproval', 'ActsAutonomously']);
export type AgentAutonomy = z.infer<typeof AgentAutonomy>;

export const AgentKind = z.enum(['ScheduledAutomation', 'RPA', 'AIAgent']);
export type AgentKind = z.infer<typeof AgentKind>;

/** The highest classification an agent is permitted to read. */
export const AgentDataReach = z.enum(['Public', 'Internal', 'Confidential', 'Restricted']);
export type AgentDataReach = z.infer<typeof AgentDataReach>;

/** What an agent does to a thing. Reading a roster is not rewriting one. */
export const AgentTouch = z.enum(['Reads', 'Writes', 'Drafts', 'Decides']);
export type AgentTouch = z.infer<typeof AgentTouch>;

export const AgentTouchpoint = z.object({
  entityId: Uuid,
  entityType: z.string(),
  name: z.string(),
  touch: AgentTouch,
  note: z.string().nullable(),
  route: z.string().nullable(),
});
export type AgentTouchpoint = z.infer<typeof AgentTouchpoint>;

export const AgentHandoff = z.object({
  agentId: Uuid,
  businessCode: z.string(),
  name: z.string(),
  note: z.string().nullable(),
  /** True where the agent on the other end acts without a human. */
  downstreamActsAutonomously: z.boolean(),
});
export type AgentHandoff = z.infer<typeof AgentHandoff>;

export const AgentRow = z.object({
  id: Uuid,
  businessCode: z.string(),
  name: z.string(),
  kind: AgentKind,
  scope: AgentScope,
  autonomy: AgentAutonomy,
  dataReach: AgentDataReach,
  /** Null for an automation that carries no model — not every agent is one. */
  modelFamily: z.string().nullable(),
  owner: z.string().nullable(),
  team: z.string().nullable(),
  inService: z.boolean(),
  commissionedOn: IsoDate.nullable(),
  /** 0–100, or null — in which case the reason is required, never a blank. */
  accuracy: z.number().min(0).max(100).nullable(),
  lastEvaluatedOn: IsoDate.nullable(),
  evaluationAbsentReason: z.string().nullable(),
  touchpoints: z.array(AgentTouchpoint),
  /** Agents this one feeds, and agents that feed it. */
  feeds: z.array(AgentHandoff),
  fedBy: z.array(AgentHandoff),
  /**
   * The single judgement this screen makes, and it is a conjunction of three
   * recorded facts rather than a score: acts without a human, on data above
   * Internal, with no evaluation on record. Nothing is inferred — if any of the
   * three is absent the flag is false, because an agent nobody has described is
   * not thereby safe, and saying otherwise would be the invention this product
   * refuses.
   */
  unreviewedAndActing: z.boolean(),
});
export type AgentRow = z.infer<typeof AgentRow>;

/** One pair of agents that act on the same thing — where overlap hides. */
export const AgentOverlap = z.object({
  entityId: Uuid,
  name: z.string(),
  entityType: z.string(),
  agents: z.array(z.object({ id: Uuid, businessCode: z.string(), name: z.string(), touch: AgentTouch, scope: AgentScope })),
});
export type AgentOverlap = z.infer<typeof AgentOverlap>;

export const AgentEstateView = z.object({
  rows: z.array(AgentRow),
  totals: z.object({
    agents: z.number().int().nonnegative(),
    personal: z.number().int().nonnegative(),
    corporate: z.number().int().nonnegative(),
    actingAutonomously: z.number().int().nonnegative(),
    /** Above Internal: Confidential or Restricted. */
    aboveInternal: z.number().int().nonnegative(),
    unevaluated: z.number().int().nonnegative(),
    /** The conjunction. The headline, counted once so the screen cannot drift. */
    unreviewedAndActing: z.number().int().nonnegative(),
    handoffs: z.number().int().nonnegative(),
  }),
  /**
   * Things more than one agent acts on.
   *
   * Overlap is not duplication and the screen does not call it that — two
   * agents reading one product may be entirely reasonable. It is the place to
   * look, which is a different claim and the only one the data supports.
   */
  overlaps: z.array(AgentOverlap),
  /** How the headline was arrived at, stated once rather than per row. */
  method: z.string(),
});
export type AgentEstateView = z.infer<typeof AgentEstateView>;
